import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CampaignDto, CampaignGroupListItemDto, ConfiguratorEtlService, ConfiguratorScenarioService, DatastoreDto, EngineCampaignGroupService, EngineCampaignService, EtlConf, Partner, ScenarioDTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, MessageService, MenuItem, SelectItem} from 'primeng/api';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {CAMPAIGN_COLUMNS, CampaignColumnDef, resolveCampaignColumnLayout} from './campaign-columns.config';
import {CampaignListColumnLayout, CampaignListPreferenceService} from './campaign-list-preference.service';

@Component({
    templateUrl: './campaign.component.html',
    styleUrls: ['./campaign.component.scss'],
    standalone: false
})
export class CampaignComponent implements OnInit, OnDestroy {
    private readonly allStatusToken = '__ALL__';
    private langChangeSub?: Subscription;

    breadcrumb: {label: string}[] = [];
    description = '';
    tenant: string;
    partner: string;
    campaigns: CampaignDto[] = [];
    filteredCampaigns: CampaignDto[] = [];
    statusEnum = CampaignDto.StatusEnum;
    scenarios: ScenarioDTO[] = [];
    scenarioMap: Map<string, string> = new Map();
    importEtlNameById: Map<string, string> = new Map();
    exportEtlNameById: Map<string, string> = new Map();
    utmOptions: Record<string, SelectItem[]> = {
        utmSource: [],
        utmMedium: [],
        utmCampaign: [],
        utmTerm: [],
        utmContent: []
    };
    filteredUtmSuggestions: Record<'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent', string[]> = {
        utmSource: [],
        utmMedium: [],
        utmCampaign: [],
        utmTerm: [],
        utmContent: []
    };
    utmKeys: Array<'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent'> =
        ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'];
    scenarioOptions: SelectItem[] = [];
    scenarioSuggestions: string[] = [];
    filteredScenarioSuggestions: string[] = [];
    statusOptions: SelectItem[] = [];
    quickStatusBaseOptions: SelectItem[] = [];
    selectedQuickStatuses: Array<string | CampaignDto.StatusEnum> = [];
    private previousQuickStatuses: Array<string | CampaignDto.StatusEnum> = [];
    showArchived: boolean = false;
    computedStatusOptions: Array<SelectItem & { disabled?: boolean }> = [];
    rowsPerPageOptions = [10, 25, 50];
    selectedCampaigns: CampaignDto[] = [];
    uploadingFile = false;
    campaignsLoading = false;
    exportMenuItems: MenuItem[] = [];
    private actionItemsByCampaignId: Record<string, MenuItem[]> = {};
    campaignColumns: CampaignColumnDef[] = CAMPAIGN_COLUMNS;
    columnLayout: CampaignListColumnLayout;
    columnManagerDialog = false;
    managerColumns: CampaignColumnDef[] = [];
    managerSelectedColumns: CampaignColumnDef[] = [];

    @ViewChild('campaignFileInput') campaignFileInput: ElementRef;

    campaignGroupOptions: SelectItem[] = [];

    filters = {
        searchText: '',
        campaignGroupId: null as number | null,
        utmSource: '',
        utmMedium: '',
        utmCampaign: '',
        utmTerm: '',
        utmContent: '',
        flux: '',
        scenario: '',
        dataType: '' as string | CampaignDto.DataTypeEnum,
        product: '' as string | CampaignDto.ProductEnum,
        importFlux: '',
        exportFlux: '',
        status: [
            CampaignDto.StatusEnum.DRAFT,
            CampaignDto.StatusEnum.INPROGRESS,
            CampaignDto.StatusEnum.SUSPENDED,
            CampaignDto.StatusEnum.TERMINATED
        ],
        first: 0,
        rows: 10
    };
    dataTypeFilterOptions: SelectItem[] = [];
    productFilterOptions: SelectItem[] = [];
    fluxFilterOptions: SelectItem[] = [];
    importFluxFilterOptions: SelectItem[] = [];
    exportFluxFilterOptions: SelectItem[] = [];
    importFluxSuggestions: string[] = [];
    filteredImportFluxSuggestions: string[] = [];
    exportFluxSuggestions: string[] = [];
    filteredExportFluxSuggestions: string[] = [];
    state: { message: string };

    constructor(private readonly engineCampaignService: EngineCampaignService,
                private readonly engineCampaignGroupService: EngineCampaignGroupService,
                private readonly configuratorScenarioService: ConfiguratorScenarioService,
                private readonly configuratorEtlService: ConfiguratorEtlService,
                private readonly campaignListPreferenceService: CampaignListPreferenceService,
                private readonly confirmationService: ConfirmationService,
                private readonly messageService: MessageService,
                private router: Router,
                private readonly translate: TranslateService
    ) {
        this.columnLayout = this.campaignListPreferenceService.getDefaultLayout();
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state as { message: string } | undefined;
        this.state = state;
    }

    ngOnInit(): void {
        this.rebuildI18nLabels();
        this.langChangeSub = this.translate.onLangChange.subscribe(() => {
            this.rebuildI18nLabels();
            if (this.campaigns?.length) {
                this.buildFilterOptions();
            }
        });
        if (this.state?.message === 'success_created') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.t('message.success'),
                detail: this.t('message.created')
            }));
        } else if (this.state?.message === 'success_updated') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.t('message.success'),
                detail: this.t('message.updated')
            }));
        }
        this.loadPersistedFilters();
        this.syncTopStatusButtonsFromStatusFilter();
        this.initExportMenu();
        this.loadColumnPreferences();
        this.update();
    }

    ngOnDestroy(): void {
        this.langChangeSub?.unsubscribe();
    }

    private rebuildI18nLabels(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.marketing.label')},
            {label: this.translate.instant('menu.marketing.campaigns')}
        ];
        this.description = this.t('description');
        this.statusOptions = [
            {label: this.statusLabel(CampaignDto.StatusEnum.DRAFT), value: CampaignDto.StatusEnum.DRAFT},
            {label: this.statusLabel(CampaignDto.StatusEnum.INPROGRESS), value: CampaignDto.StatusEnum.INPROGRESS},
            {label: this.statusLabel(CampaignDto.StatusEnum.SUSPENDED), value: CampaignDto.StatusEnum.SUSPENDED},
            {label: this.statusLabel(CampaignDto.StatusEnum.TERMINATED), value: CampaignDto.StatusEnum.TERMINATED},
            {label: this.statusLabel(CampaignDto.StatusEnum.ARCHIVED), value: CampaignDto.StatusEnum.ARCHIVED}
        ];
        this.quickStatusBaseOptions = [
            {label: this.t('filter.statusAll'), value: this.allStatusToken},
            {label: this.statusLabel(CampaignDto.StatusEnum.DRAFT), value: CampaignDto.StatusEnum.DRAFT},
            {label: this.statusLabel(CampaignDto.StatusEnum.INPROGRESS), value: CampaignDto.StatusEnum.INPROGRESS},
            {label: this.statusLabel(CampaignDto.StatusEnum.SUSPENDED), value: CampaignDto.StatusEnum.SUSPENDED},
            {label: this.statusLabel(CampaignDto.StatusEnum.TERMINATED), value: CampaignDto.StatusEnum.TERMINATED},
            {label: this.statusLabel(CampaignDto.StatusEnum.ARCHIVED), value: CampaignDto.StatusEnum.ARCHIVED}
        ];
        this.updateStatusOptions();
        this.refreshColumnLabels();
        this.initExportMenu();
    }

    private refreshColumnLabels(): void {
        this.campaignColumns = CAMPAIGN_COLUMNS.map((column) => ({
            ...column,
            label: this.translate.instant(`campaign.list.column.${column.key}`)
        }));
    }

    t(key: string, params?: Record<string, unknown>): string {
        return this.translate.instant(`campaign.list.${key}`, params);
    }

    statusLabel(status?: string): string {
        return status ? this.translate.instant(`campaign.status.${status}`) : '—';
    }

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.t('action.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.t('action.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedCampaigns(),
                disabled: !this.selectedCampaigns || this.selectedCampaigns.length === 0
            },
            {
                label: this.t('action.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllCampaigns(),
                disabled: !this.filteredCampaigns || this.filteredCampaigns.length === 0
            }
        ];
    }

    updateTenant(tenant: DatastoreDto) {
        this.tenant = tenant ? tenant.code : null;
        // Load persisted filters for this tenant/partner combination
        this.loadPersistedFilters();
        this.loadColumnPreferences();
        this.update();
    }

    updateParner(partner: Partner | Partner[] | null) {
        const selected = Array.isArray(partner) ? partner[0] : partner;
        this.partner = selected ? selected.code : null;
        if (!this.partner) {
            this.filters.campaignGroupId = null;
        }
        // Load persisted filters for this tenant/partner combination
        this.loadPersistedFilters();
        this.loadColumnPreferences();
        this.update();
    }

    private loadColumnPreferences(): void {
        if (!this.tenant) {
            this.columnLayout = this.campaignListPreferenceService.getDefaultLayout();
            return;
        }
        this.campaignListPreferenceService.load(this.tenant, this.partner).subscribe({
            next: (layout) => {
                this.columnLayout = layout;
            },
            error: () => {
                this.columnLayout = this.campaignListPreferenceService.getDefaultLayout();
            }
        });
    }

    getDisplayedColumnDefinitions(): CampaignColumnDef[] {
        const defByKey = new Map(this.campaignColumns.map((column) => [column.key, column]));
        return this.columnLayout.orderedColumnKeys
            .map((key) => defByKey.get(key))
            .filter((column): column is CampaignColumnDef => !!column);
    }

    getScrollableColumnDefinitions(): CampaignColumnDef[] {
        return this.getDisplayedColumnDefinitions().filter((column) => column.key !== 'status');
    }

    isStatusColumnDisplayed(): boolean {
        return this.getDisplayedColumnDefinitions().some((column) => column.key === 'status');
    }

    getStatusColumnDefinition(): CampaignColumnDef | undefined {
        return this.getDisplayedColumnDefinitions().find((column) => column.key === 'status');
    }

    getCampaignStatusIcon(status: CampaignDto.StatusEnum | string | undefined): string {
        switch (status) {
            case CampaignDto.StatusEnum.DRAFT:
                return 'fas fa-pen';
            case CampaignDto.StatusEnum.INPROGRESS:
                return 'fas fa-play';
            case CampaignDto.StatusEnum.SUSPENDED:
                return 'fas fa-pause';
            case CampaignDto.StatusEnum.TERMINATED:
                return 'fas fa-stop';
            case CampaignDto.StatusEnum.ARCHIVED:
                return 'fas fa-box';
            case CampaignDto.StatusEnum.DELETED:
                return 'fas fa-trash';
            default:
                return 'fas fa-circle';
        }
    }

    get campaignTableMinWidth(): string {
        const columnCount = this.getDisplayedColumnDefinitions().length;
        return `${22 + columnCount * 9}rem`;
    }

    openColumnManager(): void {
        const displayed = this.getDisplayedColumnDefinitions();
        const displayedKeys = new Set(displayed.map((column) => column.key));
        const reorderableDisplayed = displayed.filter((column) => !column.fixedLast);
        const unselected = this.campaignColumns.filter((column) => !displayedKeys.has(column.key) && !column.fixedLast);
        const fixedLastColumns = this.campaignColumns.filter((column) => column.fixedLast);
        this.managerSelectedColumns = [...displayed];
        this.managerColumns = [...reorderableDisplayed, ...unselected, ...fixedLastColumns];
        this.columnManagerDialog = true;
    }

    isColumnLocked(column: CampaignColumnDef): boolean {
        return !!column.locked;
    }

    isColumnFixedLast(column: CampaignColumnDef): boolean {
        return !!column.fixedLast;
    }

    onManagerSelectionChange(): void {
        this.campaignColumns
            .filter((column) => column.locked)
            .forEach((column) => {
                if (!this.managerSelectedColumns.some((selected) => selected.key === column.key)) {
                    this.managerSelectedColumns = [...this.managerSelectedColumns, column];
                }
            });
        this.applyManagerColumnLayout();
    }

    onManagerColumnsReorder(): void {
        const selectedKeys = new Set(this.managerSelectedColumns.map((column) => column.key));
        this.managerSelectedColumns = this.managerColumns.filter((column) => selectedKeys.has(column.key));
        this.normalizeManagerColumnOrder();
        this.applyManagerColumnLayout();
    }

    private normalizeManagerColumnOrder(): void {
        const fixedLastKeys = new Set(this.campaignColumns.filter((column) => column.fixedLast).map((column) => column.key));
        const reorderable = this.managerColumns.filter((column) => !fixedLastKeys.has(column.key));
        const fixedLast = this.campaignColumns.filter((column) => column.fixedLast);
        this.managerColumns = [...reorderable, ...fixedLast];
    }

    private applyManagerColumnLayout(): void {
        const visibleKeys = this.managerSelectedColumns.map((column) => column.key);
        const orderedKeys = this.managerColumns
            .filter((column) => visibleKeys.includes(column.key))
            .map((column) => column.key);
        this.columnLayout = resolveCampaignColumnLayout(visibleKeys, orderedKeys);
    }

    saveColumnDisplayPreferences(): void {
        if (!this.tenant) {
            return;
        }
        this.campaignListPreferenceService.save(this.tenant, this.partner, this.columnLayout).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.t('message.success'),
                    detail: this.t('message.columnsSaved')
                });
                this.columnManagerDialog = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('message.error'),
                    detail: this.t('message.columnsSaveFailed')
                });
            }
        });
    }

    getCampaignGroupNamesLabel(campaign: CampaignDto): string {
        const names = campaign.campaignGroupNames;
        if (!names?.length) {
            return '-';
        }
        return names.join(', ');
    }

    getCampaignReferenceLabel(campaign: CampaignDto): string {
        if (campaign.flux === CampaignDto.FluxEnum.IMPORT) {
            return this.getImportName(campaign.importEtl);
        }
        if (campaign.flux === CampaignDto.FluxEnum.EXPORTSCENARIO) {
            return this.getScenarioName(campaign.scenario);
        }
        if (campaign.flux === CampaignDto.FluxEnum.EXPORTDIRECT) {
            return this.getExportName(campaign.exportEtl);
        }
        return '-';
    }

    update() {
        if (!this.tenant || !this.partner) {
            this.campaignsLoading = false;
            this.clearCampaignListData();
            this.loadCampaignGroupOptions();
            return;
        }

        this.campaignsLoading = true;
        const importEtls$ = this.configuratorEtlService.getAllEtls(this.tenant, ['IMPORT']).pipe(
                catchError((error) => {
                    console.error('Error loading import ETLs:', error);
                    return of([] as EtlConf[]);
                })
            );
            const exportEtls$ = this.configuratorEtlService.getAllEtls(this.tenant, ['EXPORT']).pipe(
                catchError((error) => {
                    console.error('Error loading export ETLs:', error);
                    return of([] as EtlConf[]);
                })
            );
            const scenarios$ = this.configuratorScenarioService.listOrLatest(this.tenant, this.partner).pipe(
                    catchError((error) => {
                        console.error('Error loading scenarios:', error);
                        return of([] as ScenarioDTO[]);
                    })
                );

            forkJoin({
                importEtls: importEtls$,
                exportEtls: exportEtls$,
                scenarios: scenarios$
            }).subscribe({
                next: ({ importEtls, exportEtls, scenarios }) => {
                this.importEtlNameById.clear();
                (importEtls || []).forEach(e => this.importEtlNameById.set(e.id, e.name || e.code || e.id));

                this.exportEtlNameById.clear();
                (exportEtls || []).forEach(e => this.exportEtlNameById.set(e.id, e.name || e.code || e.id));

                this.scenarios = scenarios || [];
                this.scenarioMap.clear();
                this.scenarios.forEach((scenario) => {
                    this.scenarioMap.set(scenario.scenarioId, scenario.name);
                });

                this.loadCampaigns();
            },
            error: () => {
                this.campaignsLoading = false;
            }
        });
        this.loadCampaignGroupOptions();
    }

    private clearCampaignListData(): void {
        this.campaigns = [];
        this.filteredCampaigns = [];
        this.selectedCampaigns = [];
        this.scenarios = [];
        this.scenarioMap.clear();
        this.importEtlNameById.clear();
        this.exportEtlNameById.clear();
        this.scenarioOptions = [];
        this.scenarioSuggestions = [];
        this.filteredScenarioSuggestions = [];
        this.campaignGroupOptions = [];
    }

    private loadCampaigns(): void {
        if (!this.tenant || !this.partner) {
            this.campaigns = [];
            this.filteredCampaigns = [];
            this.campaignsLoading = false;
            return;
        }

        this.campaignsLoading = true;
        const campaignGroupId = this.filters.campaignGroupId ?? undefined;
        this.engineCampaignService.findAll(this.tenant, this.partner, campaignGroupId).subscribe({
            next: (res) => {
                this.campaigns = res;
                this.actionItemsByCampaignId = {};
                this.buildUtmOptions();
                this.buildScenarioOptions();
                this.buildFilterOptions();
                this.applyFilters(false);
                this.campaignsLoading = false;
            },
            error: () => {
                this.campaignsLoading = false;
            }
        });
    }

    private loadCampaignGroupOptions(): void {
        if (!this.tenant || !this.partner) {
            this.campaignGroupOptions = [];
            if (this.filters.campaignGroupId) {
                this.filters.campaignGroupId = null;
            }
            return;
        }

        this.engineCampaignGroupService.findAll(this.tenant, this.partner, undefined, 0, 500).subscribe({
            next: (groups: CampaignGroupListItemDto[]) => {
                this.campaignGroupOptions = (groups || []).map((group) => ({
                    label: group.name || group.code,
                    value: group.id
                }));
                this.ensureCampaignGroupFilterIsValid();
            },
            error: () => {
                this.campaignGroupOptions = [];
            }
        });
    }

    private ensureCampaignGroupFilterIsValid(): void {
        if (!this.filters.campaignGroupId) {
            return;
        }
        const stillValid = this.campaignGroupOptions.some((option) => option.value === this.filters.campaignGroupId);
        if (!stillValid) {
            this.filters.campaignGroupId = null;
            this.loadCampaigns();
        }
    }

    navigateToDetail(campaign: CampaignDto) {
        this.router.navigate(['/h/marketing/campaign/', this.tenant, campaign.id]);
    }

    getImportName(importEtlId: string | undefined): string {
        if (!importEtlId) return '-';
        return this.importEtlNameById.get(importEtlId) || importEtlId;
    }

    getExportName(exportEtlId: string | undefined): string {
        if (!exportEtlId) return '-';
        return this.exportEtlNameById.get(exportEtlId) || exportEtlId;
    }

    getCampaignModeIcon(mode: CampaignDto.CampaignModeEnum | string | undefined): string {
        if (mode === 'BUDGET_MAX') return 'pi pi-wallet';
        if (mode === 'UNITS_MAX') return 'pi pi-user';
        return 'pi pi-clock';
    }

    getCampaignModeUiLabel(campaign: CampaignDto): string {
        const mode = campaign?.campaignMode;
        const dataType = campaign?.dataType;
        if (mode === 'BUDGET_MAX') {
            return this.translate.instant('campaign.campaignMode.BUDGET_MAX');
        }
        if (mode === 'UNITS_MAX') {
            return dataType === CampaignDto.DataTypeEnum.ORDERS
                ? this.translate.instant('campaign.campaignMode.UNITS_MAX_ORDERS')
                : this.translate.instant('campaign.campaignMode.UNITS_MAX_CONTACTS');
        }
        if (mode === 'SPREAD_EXPENSE') {
            return this.translate.instant('campaign.campaignMode.SPREAD_EXPENSE');
        }
        return mode || '—';
    }

    onSearchChange(value: string) {
        this.filters.searchText = value;
        this.applyFilters(true);
    }

    clearSearch() {
        this.onSearchChange('');
    }

    onCampaignGroupChange(value: number | null) {
        this.filters.campaignGroupId = value;
        this.filters.first = 0;
        this.loadCampaigns();
    }

    clearCampaignGroup() {
        this.onCampaignGroupChange(null);
    }

    onUtmChange(key: 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent', value: string) {
        this.filters[key] = value;
        this.applyFilters(true);
    }

    clearUtm(key: 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent') {
        this.onUtmChange(key, '');
    }

    onUtmAutocompleteQuery(
        key: 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent',
        event: { query: string }
    ) {
        const query = (event?.query || '').toLowerCase().trim();
        const allValues = (this.utmOptions[key] || []).map((item) => String(item.value || item.label || ''));
        this.filteredUtmSuggestions[key] = query
            ? allValues.filter((value) => value.toLowerCase().includes(query))
            : allValues;
    }

    onScenarioChange(value: string) {
        this.filters.scenario = this.resolveScenarioFilterValue(value);
        this.applyFilters(true);
    }

    getScenarioFilterDisplay(): string {
        const value = this.filters.scenario;
        if (!value) {
            return '';
        }
        return this.scenarioMap.get(value) || value;
    }

    private resolveScenarioFilterValue(value: string): string {
        if (!value) {
            return '';
        }
        if (this.scenarioMap.has(value)) {
            return value;
        }
        const byName = this.scenarios.find((scenario) => scenario.name === value);
        if (byName) {
            return byName.scenarioId;
        }
        const option = this.scenarioOptions.find((item) => item.label === value);
        if (option?.value) {
            return String(option.value);
        }
        return value;
    }

    clearScenario() {
        this.onScenarioChange('');
    }

    onDataTypeChange(value: string | CampaignDto.DataTypeEnum) {
        this.filters.dataType = value;
        this.applyFilters(true);
    }

    onProductChange(value: string | CampaignDto.ProductEnum) {
        this.filters.product = value;
        this.refreshProductDependentFilterOptions();
        this.applyFilters(true);
    }

    onFluxChange(value: string | CampaignDto.FluxEnum) {
        this.filters.flux = value;
        this.syncFluxDependentFilters();
        this.applyFilters(true);
    }

    onImportFluxChange(value: string) {
        this.filters.importFlux = value;
        this.applyFilters(true);
    }

    clearImportFlux() {
        this.onImportFluxChange('');
    }

    onExportFluxChange(value: string) {
        this.filters.exportFlux = value;
        this.applyFilters(true);
    }

    clearExportFlux() {
        this.onExportFluxChange('');
    }

    onStatusChange(values: CampaignDto.StatusEnum[]) {
        const nextValues = values && values.length ? values : this.getQuickStatusValues();
        this.showArchived = nextValues.includes(CampaignDto.StatusEnum.ARCHIVED);
        this.updateStatusOptions();
        this.filters.status = nextValues;
        this.syncTopStatusButtonsFromStatusFilter();
        this.applyFilters(true);
    }

    onQuickStatusChange() {
        const current = this.selectedQuickStatuses || [];
        const previous = this.previousQuickStatuses || [];
        const added = current.filter((v) => !previous.includes(v));
        const removed = previous.filter((v) => !current.includes(v));
        const clicked = added[0] ?? removed[0] ?? current[0] ?? previous[0];

        if (!clicked) {
            return;
        }

        // Top tabs act like single-select (radio-like), except "Tous" which maps to all statuses.
        this.selectedQuickStatuses = [clicked];
        this.syncStatusFilterFromTopButtons();
        this.previousQuickStatuses = [...this.selectedQuickStatuses];
        this.applyFilters(true);
    }

    onPageChange(event: {first?: number, rows?: number}) {
        this.filters.first = event.first ?? 0;
        this.filters.rows = event.rows ?? this.filters.rows;
        this.persistFilters();
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }

    editCampaign(campaign: CampaignDto) {
        this.router.navigate(['/h/marketing/campaign/', this.tenant, campaign.id, 'edit']);
    }

    viewCampaign(campaign: CampaignDto) {
        this.router.navigate(['/h/marketing/campaign/', this.tenant, campaign.id]);
    }

    publishCampaign(campaign: CampaignDto) {
        const updatedCampaign: CampaignDto = {...campaign, status: CampaignDto.StatusEnum.INPROGRESS};
        this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: this.t('message.success'),
                detail: this.t('message.published')
            });
            this.update();
        }, () => {
            this.messageService.add({
                severity: 'error',
                summary: this.t('message.error'),
                detail: this.t('message.publishFailed')
            });
        });
    }

    terminateCampaign(campaign: CampaignDto) {
        this.confirmationService.confirm({
            message: this.t('dialog.terminateMessage', {name: campaign.name}),
            header: this.t('dialog.terminateTitle'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                const updatedCampaign: CampaignDto = {...campaign, status: CampaignDto.StatusEnum.TERMINATED};
                this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: this.t('message.success'),
                        detail: this.t('message.terminated')
                    });
                    this.update();
                }, () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.t('message.error'),
                        detail: this.t('message.terminateFailed')
                    });
                });
            }
        });
    }

    updateStatus(campaign: CampaignDto, status: CampaignDto.StatusEnum) {
        const updatedCampaign: CampaignDto = {...campaign, status};
        this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: this.t('message.success'),
                detail: this.t('message.statusUpdated', {status: this.statusLabel(status)})
            });
            this.update();
        }, () => {
            this.messageService.add({
                severity: 'error',
                summary: this.t('message.error'),
                detail: this.t('message.statusUpdateFailed')
            });
        });
    }

    deleteCampaign(campaign: CampaignDto) {
        this.confirmationService.confirm({
            message: this.t('dialog.deleteMessage', {name: campaign.name}),
            accept: () => this.updateStatus(campaign, CampaignDto.StatusEnum.DELETED)
        });
    }

    getCampaignActionItems(campaign: CampaignDto): MenuItem[] {
        const cached = this.actionItemsByCampaignId[campaign.id];
        if (cached) {
            return cached;
        }

        const status = campaign.status;
        const items: MenuItem[] = [
            {
                label: this.t('action.details'),
                icon: 'fas fa-eye',
                permission: 'CAMPAIGNS.read',
                command: () => this.viewCampaign(campaign)
            },
            {
                label: this.t('action.exportJson'),
                icon: 'fas fa-download',
                permission: 'CAMPAIGNS.read',
                command: () => this.exportCampaignJson(campaign)
            }
        ];

        if (status === this.statusEnum.DRAFT || status === this.statusEnum.SUSPENDED) {
            items.push(
                {
                    label: this.t('action.publish'),
                    icon: 'fas fa-play',
                    permission: 'CAMPAIGNS.edit',
                    command: () => this.publishCampaign(campaign)
                },
                {
                    label: this.t('action.edit'),
                    icon: 'fas fa-pen',
                    permission: 'CAMPAIGNS.edit',
                    command: () => this.editCampaign(campaign)
                }
            );
        }

        if (status === this.statusEnum.INPROGRESS || status === this.statusEnum.SUSPENDED) {
            items.push({
                label: this.t('action.terminate'),
                icon: 'fas fa-stop',
                permission: 'CAMPAIGNS.edit',
                command: () => this.terminateCampaign(campaign)
            });
        }

        if (status === this.statusEnum.INPROGRESS) {
            items.push({
                label: this.t('action.suspend'),
                icon: 'fas fa-pause',
                permission: 'CAMPAIGNS.edit',
                command: () => this.updateStatus(campaign, this.statusEnum.SUSPENDED)
            });
        }

        if (status === this.statusEnum.TERMINATED) {
            items.push({
                label: this.t('action.archive'),
                icon: 'fas fa-box',
                permission: 'CAMPAIGNS.edit',
                command: () => this.updateStatus(campaign, this.statusEnum.ARCHIVED)
            });
        }

        if (status === this.statusEnum.ARCHIVED) {
            items.push({
                label: this.t('action.delete'),
                icon: 'fas fa-trash',
                permission: 'CAMPAIGNS.edit',
                command: () => this.deleteCampaign(campaign)
            });
        }

        this.actionItemsByCampaignId[campaign.id] = items;
        return items;
    }

    filterStatus() {
        this.syncStatusFilterFromTopButtons();
        this.applyFilters(true);
    }

    private updateStatusOptions(): void {
        this.computedStatusOptions = this.quickStatusBaseOptions.map((o) => {
            if (o.value === CampaignDto.StatusEnum.ARCHIVED) {
                return { ...o, disabled: !this.showArchived };
            }
            return o;
        });
    }

    onToggleArchived(): void {
        const wasAllSelected = this.selectedQuickStatuses.includes(this.allStatusToken);
        this.updateStatusOptions();
        if (!this.showArchived) {
            this.filters.status = this.filters.status.filter((s) => s !== CampaignDto.StatusEnum.ARCHIVED);
        } else if (wasAllSelected && !this.filters.status.includes(CampaignDto.StatusEnum.ARCHIVED)) {
            this.filters.status = [...this.filters.status, CampaignDto.StatusEnum.ARCHIVED];
        }
        this.syncTopStatusButtonsFromStatusFilter();
        this.applyFilters(true);
    }

    isImportFluxSelected(): boolean {
        return this.filters.flux === CampaignDto.FluxEnum.IMPORT;
    }

    isAnyExportFluxSelected(): boolean {
        return this.filters.flux === CampaignDto.FluxEnum.EXPORTSCENARIO
            || this.filters.flux === CampaignDto.FluxEnum.EXPORTDIRECT;
    }

    isExportDirectFluxSelected(): boolean {
        return this.filters.flux === CampaignDto.FluxEnum.EXPORTDIRECT;
    }

    isExportScenarioFluxSelected(): boolean {
        return this.filters.flux === CampaignDto.FluxEnum.EXPORTSCENARIO;
    }

    getScenarioName(scenarioId: string): string {
        return this.scenarioMap.get(scenarioId) || scenarioId;
    }

    resetFilters(): void {
        this.showArchived = false;
        this.updateStatusOptions();
        this.syncTopStatusButtonsFromStatusFilter();
    }

    private buildUtmOptions() {
        const keys: Array<keyof CampaignComponent['utmOptions']> = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'];
        keys.forEach((key) => {
            const values = Array.from(new Set((this.campaigns || [])
                .filter(c => c.status !== CampaignDto.StatusEnum.DELETED)
                .map(c => (c as any)[key])
                .filter(v => !!v))) as string[];
            this.utmOptions[key] = values.map(v => ({label: v, value: v}));
            this.filteredUtmSuggestions[key] = values;
        });
    }

    private buildScenarioOptions() {
        const scenarioIds = Array.from(new Set(this.getCampaignsForCurrentProduct()
            .filter(c => c.flux === CampaignDto.FluxEnum.EXPORTSCENARIO && c.scenario)
            .map(c => c.scenario))) as string[];
        this.scenarioOptions = scenarioIds.map(scenarioId => ({
            label: this.scenarioMap.get(scenarioId) || scenarioId,
            value: scenarioId
        }));
        this.scenarioSuggestions = this.scenarioOptions.map(option => String(option.label || option.value || ''));
        this.filteredScenarioSuggestions = [...this.scenarioSuggestions];
    }

    private buildFilterOptions() {
        this.dataTypeFilterOptions = [
            { label: this.translate.instant('campaign.filter.dataType.contacts'), value: CampaignDto.DataTypeEnum.CONTACTS },
            { label: this.translate.instant('campaign.filter.dataType.orders'), value: CampaignDto.DataTypeEnum.ORDERS }
        ];
        this.productFilterOptions = [
            { label: this.translate.instant('campaign.product_FLIGHT'), value: CampaignDto.ProductEnum.FLIGHT },
            { label: this.translate.instant('campaign.product_HOTEL'), value: CampaignDto.ProductEnum.HOTEL },
            { label: this.translate.instant('campaign.product_CAR'), value: CampaignDto.ProductEnum.CAR },
            { label: this.translate.instant('campaign.product_PARKING'), value: CampaignDto.ProductEnum.PARKING },
            { label: this.translate.instant('campaign.product_ALL'), value: CampaignDto.ProductEnum.ALL }
        ];
        this.fluxFilterOptions = [
            { label: this.translate.instant('campaign.flux_IMPORT'), value: CampaignDto.FluxEnum.IMPORT },
            { label: this.translate.instant('campaign.flux_EXPORT_SCENARIO'), value: CampaignDto.FluxEnum.EXPORTSCENARIO },
            { label: this.translate.instant('campaign.flux_EXPORT_DIRECT'), value: CampaignDto.FluxEnum.EXPORTDIRECT }
        ];
        const productFilteredCampaigns = this.getCampaignsForCurrentProduct();
        const importIds = Array.from(new Set(productFilteredCampaigns
            .filter(c => c.flux === CampaignDto.FluxEnum.IMPORT && c.importEtl)
            .map(c => c.importEtl))) as string[];
        this.importFluxFilterOptions = [
            ...importIds.map(id => ({ label: this.importEtlNameById.get(id) || id, value: id }))
        ];
        this.importFluxSuggestions = this.importFluxFilterOptions.map(option => String(option.label || option.value || ''));
        this.filteredImportFluxSuggestions = [...this.importFluxSuggestions];
        const exportIds = Array.from(new Set(productFilteredCampaigns
            .filter(c => c.flux === CampaignDto.FluxEnum.EXPORTDIRECT && c.exportEtl)
            .map(c => c.exportEtl))) as string[];
        this.exportFluxFilterOptions = [
            ...exportIds.map(id => ({ label: this.exportEtlNameById.get(id) || id, value: id }))
        ];
        this.exportFluxSuggestions = this.exportFluxFilterOptions.map(option => String(option.label || option.value || ''));
        this.filteredExportFluxSuggestions = [...this.exportFluxSuggestions];
    }

    private getCampaignsForCurrentProduct(): CampaignDto[] {
        const selectedProduct = this.filters.product as CampaignDto.ProductEnum | '';
        if (!selectedProduct) {
            return this.campaigns || [];
        }
        return (this.campaigns || []).filter((campaign) => campaign.product === selectedProduct);
    }

    private refreshProductDependentFilterOptions(): void {
        this.buildScenarioOptions();
        this.buildFilterOptions();
        this.ensureFluxDependentFilterValuesAreValid();
    }

    private ensureFluxDependentFilterValuesAreValid(): void {
        if (this.filters.importFlux) {
            const importStillValid = this.importFluxFilterOptions.some((option) => option.value === this.filters.importFlux);
            if (!importStillValid) {
                this.filters.importFlux = '';
            }
        }

        if (this.filters.scenario) {
            const scenarioStillValid = this.scenarioOptions.some((option) => {
                const optionId = String(option.value || '');
                const optionLabel = String(option.label || '').toLowerCase();
                const filterValue = (this.filters.scenario || '').toLowerCase();
                return optionId === this.filters.scenario || optionLabel === filterValue;
            });
            if (!scenarioStillValid) {
                this.filters.scenario = '';
            }
        }

        if (this.filters.exportFlux) {
            const exportStillValid = this.exportFluxFilterOptions.some((option) => option.value === this.filters.exportFlux);
            if (!exportStillValid) {
                this.filters.exportFlux = '';
            }
        }
    }

    onScenarioAutocompleteQuery(event: { query: string }) {
        this.filteredScenarioSuggestions = this.filterTextSuggestions(this.scenarioSuggestions, event?.query);
    }

    onImportFluxAutocompleteQuery(event: { query: string }) {
        this.filteredImportFluxSuggestions = this.filterTextSuggestions(this.importFluxSuggestions, event?.query);
    }

    onExportFluxAutocompleteQuery(event: { query: string }) {
        this.filteredExportFluxSuggestions = this.filterTextSuggestions(this.exportFluxSuggestions, event?.query);
    }

    private filterTextSuggestions(values: string[], query: string | undefined): string[] {
        const normalized = (query || '').toLowerCase().trim();
        return normalized
            ? values.filter((value) => value.toLowerCase().includes(normalized))
            : [...values];
    }

    private applyFilters(resetPage: boolean) {
        if (resetPage) {
            this.filters.first = 0;
        }
        const search = this.filters.searchText?.toLowerCase().trim();
        const filtered = (this.campaigns || []).filter(c => {
            const matchesSearch = !search || [
                c.code,
                c.name,
                c.description
            ].some(field => (field || '').toLowerCase().includes(search));

            // Detailed status filter (multiSelect)
            const matchesDetailedStatus = !this.filters.status?.length
                ? true
                : this.filters.status.includes(c.status as CampaignDto.StatusEnum);
            const matchesStatus = matchesDetailedStatus;

            const scenarioFilter = (this.filters.scenario || '').toLowerCase().trim();
            const matchesScenario = !scenarioFilter
                ? true
                : (
                    c.flux === CampaignDto.FluxEnum.EXPORTSCENARIO
                    && (
                        (c.scenario || '').toLowerCase() === scenarioFilter
                        || this.getScenarioName(c.scenario).toLowerCase().includes(scenarioFilter)
                    )
                );

            const exportFluxFilter = (this.filters.exportFlux || '').toLowerCase().trim();
            const matchesExportFlux = !exportFluxFilter
                ? true
                : (
                    c.flux === CampaignDto.FluxEnum.EXPORTDIRECT
                    && (
                        (c.exportEtl || '').toLowerCase() === exportFluxFilter
                        || this.getExportName(c.exportEtl).toLowerCase().includes(exportFluxFilter)
                    )
                );

            const matchesDataType = !this.filters.dataType
                ? true
                : c.dataType === this.filters.dataType;

            const matchesProduct = !this.filters.product
                ? true
                : c.product === this.filters.product;

            const matchesFlux = !this.filters.flux
                ? true
                : c.flux === this.filters.flux;

            const importFluxFilter = (this.filters.importFlux || '').toLowerCase().trim();
            const matchesImportFlux = !importFluxFilter
                ? true
                : (
                    c.flux === CampaignDto.FluxEnum.IMPORT
                    && (
                        (c.importEtl || '').toLowerCase() === importFluxFilter
                        || this.getImportName(c.importEtl).toLowerCase().includes(importFluxFilter)
                    )
                );

            const matchesUtm = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent']
                .every((key: string) => {
                    const value = (this.filters as any)[key];
                    if (!value) {
                        return true;
                    }
                    return ((c as any)[key] || '').toLowerCase().includes((value as string).toLowerCase());
                });

            return matchesSearch
                && matchesStatus
                && matchesScenario
                && matchesExportFlux
                && matchesDataType
                && matchesProduct
                && matchesFlux
                && matchesImportFlux
                && matchesUtm;
        });

        this.filteredCampaigns = filtered;
        this.updateExportMenuState();
        this.persistFilters();
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedCampaigns || this.selectedCampaigns.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.filteredCampaigns || this.filteredCampaigns.length === 0;
            }
        }
    }

    private persistFilters() {
        const storageKey = this.getStorageKey();
        const state = {
            ...this.filters,
            selectedQuickStatuses: this.selectedQuickStatuses,
            showArchived: this.showArchived
        };
        localStorage.setItem(storageKey, JSON.stringify(state));
    }

    private loadPersistedFilters() {
        const storageKey = this.getStorageKey();
        const persisted = localStorage.getItem(storageKey);
        if (persisted) {
            try {
                const parsed = JSON.parse(persisted);
                this.filters = {
                    searchText: parsed.searchText || '',
                    campaignGroupId: parsed.campaignGroupId ?? null,
                    utmSource: parsed.utmSource || '',
                    utmMedium: parsed.utmMedium || '',
                    utmCampaign: parsed.utmCampaign || '',
                    utmTerm: parsed.utmTerm || '',
                    utmContent: parsed.utmContent || '',
                    flux: parsed.flux || '',
                    scenario: parsed.scenario || '',
                    dataType: parsed.dataType || '',
                    product: parsed.product || '',
                    importFlux: parsed.importFlux || '',
                    exportFlux: parsed.exportFlux || '',
                    status: parsed.status && parsed.status.length ? parsed.status : [
                        CampaignDto.StatusEnum.DRAFT,
                        CampaignDto.StatusEnum.INPROGRESS,
                        CampaignDto.StatusEnum.SUSPENDED,
                        CampaignDto.StatusEnum.TERMINATED
                    ],
                    first: parsed.first || 0,
                    rows: parsed.rows || 10
                };
                // Load status filter state
                this.selectedQuickStatuses = Array.isArray(parsed.selectedQuickStatuses) ? parsed.selectedQuickStatuses : [];
                this.showArchived = parsed.showArchived || false;
                this.updateStatusOptions();
                this.syncFluxDependentFilters();
                this.syncTopStatusButtonsFromStatusFilter();
            } catch (e) {
                // fallback to defaults if parsing fails
            }
        }
    }

    private getQuickStatusValues(): CampaignDto.StatusEnum[] {
        const quick: CampaignDto.StatusEnum[] = [
            CampaignDto.StatusEnum.DRAFT,
            CampaignDto.StatusEnum.INPROGRESS,
            CampaignDto.StatusEnum.SUSPENDED,
            CampaignDto.StatusEnum.TERMINATED
        ];
        if (this.showArchived) {
            quick.push(CampaignDto.StatusEnum.ARCHIVED);
        }
        return quick;
    }

    private syncStatusFilterFromTopButtons(): void {
        const selected = this.selectedQuickStatuses || [];
        const quickValues = this.getQuickStatusValues();
        if (selected.includes(this.allStatusToken)) {
            this.filters.status = [...quickValues];
            this.selectedQuickStatuses = [this.allStatusToken];
            return;
        }

        this.filters.status = selected.filter((v): v is CampaignDto.StatusEnum =>
            quickValues.includes(v as CampaignDto.StatusEnum)
        );
    }

    private syncTopStatusButtonsFromStatusFilter(): void {
        const quickValues = this.getQuickStatusValues();
        const selected = quickValues.filter((status) => this.filters.status.includes(status));
        if (!this.filters.status?.length) {
            this.selectedQuickStatuses = [this.allStatusToken];
            this.previousQuickStatuses = [...this.selectedQuickStatuses];
            return;
        }
        const allSelected = quickValues.length > 0 && selected.length === quickValues.length;
        this.selectedQuickStatuses = allSelected ? [this.allStatusToken] : selected;
        this.previousQuickStatuses = [...this.selectedQuickStatuses];
    }

    private syncFluxDependentFilters(): void {
        if (this.isImportFluxSelected()) {
            this.filters.scenario = '';
            this.filters.exportFlux = '';
            return;
        }

        if (this.isAnyExportFluxSelected()) {
            this.filters.importFlux = '';
        }

        if (this.isExportDirectFluxSelected()) {
            this.filters.scenario = '';
        }

        if (this.isExportScenarioFluxSelected()) {
            this.filters.exportFlux = '';
        }
    }

    private getStorageKey(): string {
        const tenantKey = this.tenant || 'no-tenant';
        const partnerKey = this.partner || 'no-partner';
        return `campaign-list-state:${tenantKey}:${partnerKey}`;
    }

    // ============ EXPORT/IMPORT METHODS ============

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.campaignFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.t('message.error'),
                detail: this.t('message.unsupportedFormat')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.engineCampaignService.importCampaignJson(this.tenant, jsonContent).subscribe(
                (campaign: CampaignDto) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.t('message.success'),
                        detail: this.t('message.importedOne', {name: campaign.name})
                    });
                    this.update();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.t('message.error'),
                        detail: error.error?.detail || this.t('message.importFailed')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.engineCampaignService.importCampaignsZip(this.tenant, file).subscribe(
            (campaigns: CampaignDto[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.t('message.success'),
                    detail: this.t('message.importedMany', {count: campaigns.length})
                });
                this.update();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('message.error'),
                    detail: error.error?.detail || this.t('message.importFailed')
                });
            }
        );
    }

    /**
     * Exporte une seule campagne en JSON
     */
    exportCampaignJson(campaign: CampaignDto) {
        this.engineCampaignService.exportCampaignJson(this.tenant, campaign.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `campaign-${campaign.code}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.t('message.success'),
                    detail: this.t('message.exportedOne')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('message.error'),
                    detail: this.t('message.exportFailed')
                });
            }
        );
    }

    /**
     * Exporte plusieurs campagnes en ZIP
     */
    exportSelectedCampaigns() {
        if (!this.selectedCampaigns || this.selectedCampaigns.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('message.warn'),
                detail: this.t('message.noSelection')
            });
            return;
        }

        const ids = this.selectedCampaigns.map(c => c.id);
        this.engineCampaignService.exportCampaignsZip(this.tenant, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `campaigns-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.t('message.success'),
                    detail: this.t('message.exportedMany', {count: this.selectedCampaigns.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('message.error'),
                    detail: this.t('message.exportFailed')
                });
            }
        );
    }

    /**
     * Exporte toutes les campagnes filtrées en ZIP
     */
    exportAllCampaigns() {
        if (!this.filteredCampaigns || this.filteredCampaigns.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('message.warn'),
                detail: this.t('message.nothingToExport')
            });
            return;
        }

        const ids = this.filteredCampaigns.map(c => c.id);
        this.engineCampaignService.exportCampaignsZip(this.tenant, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `campaigns-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.t('message.success'),
                    detail: this.t('message.exportedMany', {count: this.filteredCampaigns.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('message.error'),
                    detail: this.t('message.exportFailed')
                });
            }
        );
    }
}
